import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiCheckCircle,
  FiPlus,
  FiTruck,
  FiArrowLeft,
  FiLock,
  FiAlertTriangle,
  FiX,
  FiHome,
  FiBriefcase
} from 'react-icons/fi';
import './CheckoutPage.css';
import { API_BASE_URL } from '../../config';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

export default function CheckoutPage({
  checkoutId,
  cartItems,
  setCartItems,
  currentUser,
  triggerNotification,
  selectedCurr,
  convertPrice,
  onBack
}) {
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [deliverySpeed, setDeliverySpeed] = useState('normal'); // 'normal' | 'fast'
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [checkoutAddresses, setCheckoutAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');

  const [accessAllowed, setAccessAllowed] = useState(true);
  const [accessDeniedReason, setAccessDeniedReason] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !currentUser) {
      setAccessAllowed(false);
      setAccessDeniedReason('AUTHENTICATION_REQUIRED');
      return;
    }
    
    if (cartItems.length === 0) {
      setAccessAllowed(false);
      setAccessDeniedReason('EMPTY_CART');
      return;
    }

    if (!checkoutId) {
      setAccessAllowed(false);
      setAccessDeniedReason('INVALID_ID');
      return;
    }

    const itemExists = cartItems.some(item => {
      const itemVarId = item.variant_id || item.id;
      return String(itemVarId) === String(checkoutId);
    });

    if (!itemExists) {
      setAccessAllowed(false);
      setAccessDeniedReason('ITEM_NOT_IN_CART');
      return;
    }

    setAccessAllowed(true);
    setAccessDeniedReason('');
  }, [checkoutId, cartItems, currentUser]);

  // Toggle states
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);

  // Address form inputs
  const [newAddrForm, setNewAddrForm] = useState({
    full_name: '',
    phone: '',
    alt_phone: '',
    pincode: '',
    address_line1: '',
    address_line2: '',
    landmark: '',
    city: '',
    state: 'Karnataka',
    address_type: 'Home',
    is_default: false,
    isBusiness: false,
    companyName: '',
    gstin: ''
  });

  // Shipping configuration
  const [shippingSettings, setShippingSettings] = useState({
    shipping_fee: 50,
    free_shipping_threshold: 500,
    tax_rate: 5
  });

  // Full products metadata for state-wise addons lookup
  const [productsList, setProductsList] = useState([]);

  // 1. Fetch addresses, settings & products on mount
  useEffect(() => {
    setCheckoutError('');
    const token = localStorage.getItem('token');

    // Fetch addresses
    if (token) {
      axios.get(`${API_BASE_URL}/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const addrs = res.data.addresses || [];
        setCheckoutAddresses(addrs);
        const defaultAddr = addrs.find(a => a.isDefault === 1 || a.isDefault === true || a.is_default === 1 || a.is_default === true);
        setSelectedAddrId(defaultAddr ? defaultAddr.id : (addrs.length > 0 ? addrs[0].id : null));
      }).catch(err => {
        console.error('Failed to load checkout addresses:', err);
        setCheckoutAddresses([]);
      });
    }

    // Fetch shipping settings
    axios.get(`${API_BASE_URL}/orders/settings/all`)
      .then(res => {
        if (res.data.settings) {
          setShippingSettings({
            shipping_fee: parseFloat(res.data.settings.shipping_fee || 50),
            free_shipping_threshold: parseFloat(res.data.settings.free_shipping_threshold || 500),
            tax_rate: parseFloat(res.data.settings.tax_rate || 5)
          });
        }
      })
      .catch(err => {
        console.error('Failed to load shipping settings:', err);
      });

    // Fetch products
    axios.get(`${API_BASE_URL}/products`)
      .then(res => {
        if (res.data.products) {
          setProductsList(res.data.products);
        }
      })
      .catch(err => {
        console.error('Failed to load products list for addons:', err);
      });

    // Load Razorpay script
    if (!document.getElementById('razorpay-script')) {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Selected address state
  const selectedAddress = checkoutAddresses.find(a => a.id === selectedAddrId);
  const shippingState = selectedAddress ? selectedAddress.state : '';

  // Match items to products list and calculate individual state-wise express addon
  const getExpressAddonForCartItem = (item, state) => {
    if (!state) return 0;

    // Find matching product in database items
    const matchedProduct = productsList.find(p => {
      // Match by product ID if present
      if (item.product_id && p.id === item.product_id) return true;
      // Match by variant ID in variants list
      if (p.variants && p.variants.some(v => v.id === item.variant_id || v.id === item.id)) return true;
      // Fallback: match by title similarity
      const pTitle = p.title.toLowerCase().replace(/[^a-z0-9]/g, '');
      const iTitle = item.title.toLowerCase().replace(/[^a-z0-9]/g, '');
      return iTitle.includes(pTitle) || pTitle.includes(iTitle);
    });

    if (!matchedProduct) return 0;

    let addons = {};
    try {
      addons = typeof matchedProduct.fast_delivery_addons === 'string'
        ? JSON.parse(matchedProduct.fast_delivery_addons)
        : (matchedProduct.fast_delivery_addons || {});
    } catch (e) { }

    return parseFloat(addons[state] || 0);
  };

  // Pricing calculations
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);

  // Calculate priority express addons total for all items in the cart
  const expressAddonTotal = cartItems.reduce((sum, item) => {
    const addon = getExpressAddonForCartItem(item, shippingState);
    return sum + (deliverySpeed === 'fast' ? addon : 0) * item.qty;
  }, 0);

  const subtotalWithAddon = subtotal + expressAddonTotal;
  const tax = parseFloat((subtotalWithAddon * (shippingSettings.tax_rate / 100)).toFixed(2));
  const shipping = subtotalWithAddon >= shippingSettings.free_shipping_threshold ? 0 : shippingSettings.shipping_fee;
  const grandTotal = subtotalWithAddon + tax + shipping;

  // Handle adding new address inline
  const handleAddNewAddressSubmit = async (e) => {
    e.preventDefault();
    setCheckoutError('');

    const token = localStorage.getItem('token');
    if (!token) {
      setCheckoutError('You must be signed in to save addresses.');
      return;
    }

    if (!newAddrForm.full_name || !newAddrForm.phone || !newAddrForm.pincode || !newAddrForm.address_line1 || !newAddrForm.city) {
      setCheckoutError('Please fill in all required fields for the new address.');
      return;
    }

    const payload = {
      fullName: newAddrForm.full_name,
      phone: newAddrForm.phone,
      altPhone: newAddrForm.alt_phone || null,
      pincode: newAddrForm.pincode,
      addressLine1: newAddrForm.address_line1,
      addressLine2: newAddrForm.address_line2,
      landmark: newAddrForm.landmark || null,
      city: newAddrForm.city,
      state: newAddrForm.state,
      addressType: newAddrForm.address_type,
      isDefault: newAddrForm.is_default ? 1 : 0,
      companyName: newAddrForm.isBusiness ? newAddrForm.companyName : null,
      gstin: newAddrForm.isBusiness ? newAddrForm.gstin : null
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/addresses`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.address) {
        triggerNotification('New shipping address added successfully.');
        // Refresh address list and select new address
        const addedAddress = res.data.address;
        setCheckoutAddresses(prev => [addedAddress, ...prev]);
        setSelectedAddrId(addedAddress.id);
        setShowNewAddressForm(false);
        // Reset form
        setNewAddrForm({
          full_name: '',
          phone: '',
          alt_phone: '',
          pincode: '',
          address_line1: '',
          address_line2: '',
          landmark: '',
          city: '',
          state: 'Karnataka',
          address_type: 'Home',
          is_default: false,
          isBusiness: false,
          companyName: '',
          gstin: ''
        });
      }
    } catch (err) {
      console.error(err);
      setCheckoutError(err.response?.data?.message || 'Failed to save address.');
    }
  };

  // Submit order placement
  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    setCheckoutError('');

    if (!selectedAddrId || checkoutAddresses.length === 0) {
      setCheckoutError('Please select or add a delivery address to place your order.');
      return;
    }

    setIsPlacingOrder(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Session expired. Please log in again.');

      const submitOrder = async (rzpPaymentId) => {
        const payload = {
          cartItems: cartItems.map(item => {
            let variantId = item.variant_id;
            if (!variantId && typeof item.id === 'string' && item.id.includes('-')) {
              variantId = parseInt(item.id.split('-').pop());
            }
            if (!variantId || isNaN(variantId)) variantId = item.id;
            return {
              product_variant_id: variantId,
              quantity: item.qty,
              price: item.price,
              sku: item.sku || 'UNKNOWN',
              title: item.title,
              image: item.image,
              delivery_speed: deliverySpeed // Pass speed selection to backend
            };
          }),
          shippingAddressId: selectedAddrId,
          billingAddressId: selectedAddrId,
          paymentMethod: rzpPaymentId ? paymentMethod : 'COD',
          transactionId: rzpPaymentId || undefined
        };

        const res = await axios.post(`${API_BASE_URL}/orders/checkout`, payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = res.data;
        triggerNotification(`Order placed successfully! Order Number: ${data.order.order_number}`);
        setCartItems([]);
        setCheckoutError('');
        // Navigate to my orders
        onBack();
      };

      // Razorpay Online Payments handler
      if (paymentMethod !== 'COD') {
        if (!window.Razorpay) {
          setCheckoutError('Payment gateway script loading failed. Please refresh and try again.');
          setIsPlacingOrder(false);
          return;
        }

        const options = {
          key: 'rzp_test_T2y1PxDWpNMPwN',
          amount: Math.round(grandTotal * 100), // convert to paise
          currency: 'INR',
          name: 'Orbifert Organic Pvt. Ltd.',
          description: `Checkout Payment (${paymentMethod})`,
          image: '/logo/orbifert-logo.png',
          prefill: {
            name: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : '',
            email: currentUser?.email || '',
            contact: currentUser?.phone_number || '',
            method: paymentMethod === 'UPI' ? 'upi' : (paymentMethod === 'Netbanking' ? 'netbanking' : 'card')
          },
          theme: { color: '#00B207' },
          handler: async function (response) {
            try {
              await submitOrder(response.razorpay_payment_id);
            } catch (err) {
              console.error(err);
              setCheckoutError(err.response?.data?.message || err.message || 'Checkout failed after payment.');
            } finally {
              setIsPlacingOrder(false);
            }
          },
          modal: {
            ondismiss: function () {
              setIsPlacingOrder(false);
              setCheckoutError('Payment window closed by user.');
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          setIsPlacingOrder(false);
          setCheckoutError(`Payment failed: ${response.error.description}`);
        });
        rzp.open();
        return;
      }

      // Direct COD submission
      await submitOrder(null);
    } catch (err) {
      console.error(err);
      setCheckoutError(err.response?.data?.message || err.message || 'Checkout failed.');
    } finally {
      if (paymentMethod === 'COD') setIsPlacingOrder(false);
    }
  };

  if (!accessAllowed) {
    return (
      <div className="checkout-page-wrapper" style={{ backgroundColor: '#fff5f5' }}>
        <header className="checkout-premium-header">
          <div className="checkout-header-container">
            <div className="checkout-brand-logo" onClick={onBack}>
              <img src="/logo/Orbifert Logo.png" alt="Orbifert" className="checkout-logo-img" />
              <span className="logo-badge">Checkout</span>
            </div>
            <div className="checkout-secure-badge">
              <FiLock className="secure-lock-icon" />
              <div className="secure-badge-texts">
                <span className="secure-title">Secure SSL</span>
                <span className="secure-subtitle">256-Bit Encrypted</span>
              </div>
            </div>
          </div>
        </header>

        <main className="checkout-main-content container flex-center" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glassmorphic-panel text-center" style={{ maxWidth: '500px', width: '90%', padding: '40px 30px', border: '1.5px solid rgba(239, 68, 68, 0.15)', boxShadow: '0 10px 30px rgba(239, 68, 68, 0.04)', borderRadius: '20px', backgroundColor: '#ffffff' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#fef2f2', border: '1.5px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <FiAlertTriangle style={{ fontSize: '28px', color: '#ef4444' }} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#991b1b', marginBottom: '14px' }}>Checkout Access Restricted</h2>
            
            {accessDeniedReason === 'AUTHENTICATION_REQUIRED' && (
              <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '24px' }}>
                You must be logged in to access this secure checkout session. Copying links or bypassing login requirements is restricted to safeguard your purchase credentials.
              </p>
            )}
            {accessDeniedReason === 'EMPTY_CART' && (
              <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '24px' }}>
                Your shopping cart is empty! Add products to your cart first before proceeding to checkout.
              </p>
            )}
            {(accessDeniedReason === 'ITEM_NOT_IN_CART' || accessDeniedReason === 'INVALID_ID') && (
              <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '24px' }}>
                Security violation: This checkout URL does not represent an item in your active shopping cart. Link manipulation or session sharing across accounts is strictly blocked.
              </p>
            )}

            <button 
              className="checkout-back-link" 
              onClick={onBack} 
              style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto', fontSize: '14px', fontWeight: '600', color: '#4d4d4d', border: 'none', background: 'none', cursor: 'pointer' }}
            >
              <FiArrowLeft /> Back to Shop
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="checkout-page-wrapper">
      {/* 1. Premium Checkout Header */}
      <header className="checkout-premium-header">
        <div className="checkout-header-container">
          <div className="checkout-brand-logo" onClick={onBack}>
            <img src="/logo/Orbifert Logo.png" alt="Orbifert" className="checkout-logo-img" />
            <span className="logo-badge">Checkout</span>
          </div>

          <div className="checkout-steps-progress">
            <div className="step-node active">
              <span className="node-number">1</span>
              <span className="node-label">Address</span>
            </div>
            <div className="step-connector active"></div>
            <div className="step-node active">
              <span className="node-number">2</span>
              <span className="node-label">Delivery</span>
            </div>
            <div className="step-connector active"></div>
            <div className="step-node active">
              <span className="node-number">3</span>
              <span className="node-label">Payment</span>
            </div>
          </div>

          <div className="checkout-secure-badge">
            <FiLock className="secure-lock-icon" />
            <div className="secure-badge-texts">
              <span className="secure-title">Secure SSL</span>
              <span className="secure-subtitle">256-Bit Encrypted</span>
            </div>
          </div>
        </div>
      </header>

      {/* 2. Main Page Layout Grid */}
      <main className="checkout-main-content container">
        <button className="checkout-back-link" onClick={onBack}>
          <FiArrowLeft className="back-arrow-icon" /> Continue Shopping
        </button>

        {checkoutError && (
          <div className="checkout-error-banner animate-fade-in">
            <FiAlertTriangle className="error-icon" />
            <span>{checkoutError}</span>
            <button className="error-close-btn" onClick={() => setCheckoutError('')}><FiX /></button>
          </div>
        )}

        <div className="checkout-grid-container">
          {/* LEFT: Steps Flow */}
          <section className="checkout-steps-section">

            {/* STEP 1: SHIPPING ADDRESS */}
            <div className="checkout-step-card glassmorphic-panel">
              <div className="step-card-header">
                <span className="step-card-number">1</span>
                <h3>Delivery Shipping Address</h3>
              </div>

              <div className="step-card-body">
                {checkoutAddresses.length === 0 && !showNewAddressForm ? (
                  <div className="checkout-address-alert">
                    <p>No saved addresses found. Please add a shipping address below to proceed.</p>
                    <button
                      type="button"
                      className="add-address-trigger-btn"
                      onClick={() => setShowNewAddressForm(true)}
                    >
                      <FiPlus /> Add Your First Address
                    </button>
                  </div>
                ) : (
                  <>
                    {!showNewAddressForm && (
                      <div className="address-cards-grid">
                        {checkoutAddresses.map(addr => (
                          <div
                            key={addr.id}
                            className={`address-selectable-card ${addr.id === selectedAddrId ? 'selected-active' : ''}`}
                            onClick={() => setSelectedAddrId(addr.id)}
                          >
                            <div className="address-card-selection-indicator">
                              <FiCheckCircle className="check-icon" />
                            </div>
                            <div className="address-card-top-row">
                              <span className="address-badge">
                                {(addr.addressType || addr.address_type) === 'Work' ? <FiBriefcase /> : <FiHome />} {(addr.addressType || addr.address_type)}
                              </span>
                              {(addr.isDefault === 1 || addr.isDefault === true || addr.is_default === 1 || addr.is_default === true) && (
                                <span className="default-address-tag">Default</span>
                              )}
                            </div>
                            
                            <h4 className="address-receiver">{addr.fullName || addr.full_name}</h4>

                            {/* Business Info Display */}
                            {(addr.companyName || addr.company_name) && (
                              <div className="address-business-tag" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '6px 8px', fontSize: '11.5px', color: '#15803d', margin: '4px 0 8px 0', lineHeight: '1.3', textAlign: 'left' }}>
                                🏢 <strong>{addr.companyName || addr.company_name}</strong>
                                {(addr.gstin) && <span> (GSTIN: {addr.gstin})</span>}
                              </div>
                            )}

                            <p className="address-street">{addr.addressLine1 || addr.address_line1}, {addr.addressLine2 || addr.address_line2}</p>
                            <p className="address-locality">{addr.city}, {addr.state} - <strong>{addr.pincode}</strong></p>
                            <p className="address-phone">📞 {addr.phone}</p>
                          </div>
                        ))}

                        <div className="address-selectable-card add-new-placeholder-card" onClick={() => setShowNewAddressForm(true)}>
                          <FiPlus className="plus-dashed-icon" />
                          <span>Add New Shipping Address</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {showNewAddressForm && (
                  <form onSubmit={handleAddNewAddressSubmit} className="new-address-inline-form animate-slide-down">
                    <div className="inline-form-header">
                      <h4>Create Shipping Profile</h4>
                      <button type="button" className="close-form-btn" onClick={() => setShowNewAddressForm(false)}>
                        <FiX />
                      </button>
                    </div>

                    <div className="form-double-row">
                      <div className="checkout-input-group">
                        <label>Receiver Full Name *</label>
                        <input
                          type="text"
                          required
                          value={newAddrForm.full_name}
                          onChange={e => setNewAddrForm({ ...newAddrForm, full_name: e.target.value })}
                          placeholder="e.g. Albert Mangang"
                        />
                      </div>
                      <div className="checkout-input-group">
                        <label>Contact Phone Number *</label>
                        <input
                          type="tel"
                          required
                          value={newAddrForm.phone}
                          onChange={e => setNewAddrForm({ ...newAddrForm, phone: e.target.value })}
                          placeholder="10-digit number"
                        />
                      </div>
                    </div>

                    <div className="form-double-row">
                      <div className="checkout-input-group">
                        <label>Alternative Phone (Optional)</label>
                        <input
                          type="tel"
                          value={newAddrForm.alt_phone}
                          onChange={e => setNewAddrForm({ ...newAddrForm, alt_phone: e.target.value })}
                          placeholder="Alt contact number"
                        />
                      </div>
                      <div className="checkout-input-group">
                        <label>Pincode / Zipcode *</label>
                        <input
                          type="text"
                          required
                          value={newAddrForm.pincode}
                          onChange={e => setNewAddrForm({ ...newAddrForm, pincode: e.target.value })}
                          placeholder="6-digit PIN"
                        />
                      </div>
                    </div>

                    <div className="checkout-input-group">
                      <label>Address Line 1 (House, Apartment, Suite) *</label>
                      <input
                        type="text"
                        required
                        value={newAddrForm.address_line1}
                        onChange={e => setNewAddrForm({ ...newAddrForm, address_line1: e.target.value })}
                        placeholder="Flat/House number, building name"
                      />
                    </div>

                    <div className="checkout-input-group">
                      <label>Address Line 2 (Area, Street, Sector, Village) *</label>
                      <input
                        type="text"
                        required
                        value={newAddrForm.address_line2}
                        onChange={e => setNewAddrForm({ ...newAddrForm, address_line2: e.target.value })}
                        placeholder="Street details, locality name"
                      />
                    </div>

                    <div className="form-triple-row">
                      <div className="checkout-input-group">
                        <label>Landmark (Optional)</label>
                        <input
                          type="text"
                          value={newAddrForm.landmark}
                          onChange={e => setNewAddrForm({ ...newAddrForm, landmark: e.target.value })}
                          placeholder="e.g. Near metro station"
                        />
                      </div>
                      <div className="checkout-input-group">
                        <label>City *</label>
                        <input
                          type="text"
                          required
                          value={newAddrForm.city}
                          onChange={e => setNewAddrForm({ ...newAddrForm, city: e.target.value })}
                          placeholder="e.g. Bengaluru"
                        />
                      </div>
                      <div className="checkout-input-group">
                        <label>State *</label>
                        <select
                          value={newAddrForm.state}
                          onChange={e => setNewAddrForm({ ...newAddrForm, state: e.target.value })}
                        >
                          {INDIAN_STATES.map(st => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-double-row flex-align-center" style={{ marginTop: '14px' }}>
                      <div className="checkout-input-group flex-row">
                        <label style={{ marginRight: '16px' }}>Address Category:</label>
                        <div className="address-type-radio-grid">
                          <button
                            type="button"
                            className={`address-type-badge-btn ${newAddrForm.address_type === 'Home' ? 'active' : ''}`}
                            onClick={() => setNewAddrForm({ ...newAddrForm, address_type: 'Home' })}
                          >
                            🏠 Home
                          </button>
                          <button
                            type="button"
                            className={`address-type-badge-btn ${newAddrForm.address_type === 'Work' ? 'active' : ''}`}
                            onClick={() => setNewAddrForm({ ...newAddrForm, address_type: 'Work' })}
                          >
                            💼 Work
                          </button>
                        </div>
                      </div>

                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#4d4d4d' }}>
                        <input
                          type="checkbox"
                          checked={newAddrForm.is_default}
                          onChange={e => setNewAddrForm({ ...newAddrForm, is_default: e.target.checked })}
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#00B207' }}
                        />
                        Set as Default Shipping Address
                      </label>
                    </div>

                    {/* Business Buy Toggle */}
                    <div className="checkout-business-buy-box" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px', marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', boxSizing: 'border-box', textAlign: 'left' }}>
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#1b5e20' }}>
                        <input
                          type="checkbox"
                          checked={newAddrForm.isBusiness}
                          onChange={e => setNewAddrForm({ ...newAddrForm, isBusiness: e.target.checked })}
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#00B207' }}
                        />
                        🏢 Buying for business? (GST Invoice)
                      </label>

                      {newAddrForm.isBusiness && (
                        <div className="form-double-row" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '14px', marginTop: '4px' }}>
                          <div className="checkout-input-group" style={{ textAlign: 'left' }}>
                            <label>Company / Registered Business Name *</label>
                            <input
                              type="text"
                              required={newAddrForm.isBusiness}
                              value={newAddrForm.companyName}
                              onChange={e => setNewAddrForm({ ...newAddrForm, companyName: e.target.value })}
                              placeholder="e.g. Acme Organic Exports"
                            />
                          </div>
                          <div className="checkout-input-group" style={{ textAlign: 'left' }}>
                            <label>GSTIN (Optional)</label>
                            <input
                              type="text"
                              value={newAddrForm.gstin}
                              onChange={e => setNewAddrForm({ ...newAddrForm, gstin: e.target.value.toUpperCase() })}
                              placeholder="15-character GSTIN"
                              maxLength={15}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="inline-form-actions">
                      <button type="button" className="btn-cancel" onClick={() => setShowNewAddressForm(false)}>Cancel</button>
                      <button type="submit" className="btn-save">Save Address</button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* STEP 2: DELIVERY SPEEDS */}
            <div className="checkout-step-card glassmorphic-panel">
              <div className="step-card-header">
                <span className="step-card-number">2</span>
                <h3>Delivery Speed Selection</h3>
              </div>

              <div className="step-card-body">
                <div className="delivery-speed-grid">
                  <div
                    className={`delivery-speed-card ${deliverySpeed === 'normal' ? 'active' : ''}`}
                    onClick={() => setDeliverySpeed('normal')}
                  >
                    <div className="speed-card-selection-dot"></div>
                    <FiTruck className="speed-card-icon" />
                    <div className="speed-card-texts">
                      <span className="speed-title">Standard Courier Delivery</span>
                      <span className="speed-time">Delivered in 5 - 7 business days</span>
                      <span className="speed-charge-desc">Included in base shipping charges</span>
                    </div>
                  </div>

                  <div
                    className={`delivery-speed-card express-glow ${deliverySpeed === 'fast' ? 'active' : ''}`}
                    onClick={() => {
                      if (!shippingState) {
                        triggerNotification('Please select or add a shipping address first to calculate Express addons.');
                        return;
                      }
                      setDeliverySpeed('fast');
                    }}
                  >
                    <div className="speed-card-selection-dot"></div>
                    <span className="express-tag-badge">🚀 Priority</span>
                    <div className="speed-card-texts">
                      <span className="speed-title">Express Delivery</span>
                      <span className="speed-time">Delivered in 1 - 2 business days</span>
                      <span className="speed-charge-desc">
                        {shippingState ? (
                          `State addon fee: + ${convertPrice(expressAddonTotal, selectedCurr)} total`
                        ) : (
                          'Select address to calculate express state addon'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 3: PAYMENT METHODS */}
            <div className="checkout-step-card glassmorphic-panel">
              <div className="step-card-header">
                <span className="step-card-number">3</span>
                <h3>Secure Payment Options</h3>
              </div>

              <div className="step-card-body">
                <div className="payment-options-grid">
                  <div
                    className={`payment-method-block ${paymentMethod === 'COD' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('COD')}
                  >
                    <span className="payment-icon">💵</span>
                    <div className="payment-texts">
                      <span className="payment-title">Cash on Delivery (COD)</span>
                      <span className="payment-subtitle">Pay with cash, UPI or card at delivery</span>
                    </div>
                    {paymentMethod === 'COD' && <div className="payment-check-corner">✓</div>}
                  </div>

                  <div
                    className={`payment-method-block ${paymentMethod === 'UPI' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('UPI')}
                  >
                    <span className="payment-icon">📱</span>
                    <div className="payment-texts">
                      <span className="payment-title">UPI / Google Pay / PhonePe</span>
                      <span className="payment-subtitle">Instant secure scanning through Razorpay</span>
                    </div>
                    {paymentMethod === 'UPI' && <div className="payment-check-corner">✓</div>}
                  </div>

                  <div
                    className={`payment-method-block ${paymentMethod === 'Card' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('Card')}
                  >
                    <span className="payment-icon">💳</span>
                    <div className="payment-texts">
                      <span className="payment-title">Credit / Debit Card</span>
                      <span className="payment-subtitle">Visa, Mastercard, RuPay, Amex via Razorpay</span>
                    </div>
                    {paymentMethod === 'Card' && <div className="payment-check-corner">✓</div>}
                  </div>

                  <div
                    className={`payment-method-block ${paymentMethod === 'Netbanking' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('Netbanking')}
                  >
                    <span className="payment-icon">🏦</span>
                    <div className="payment-texts">
                      <span className="payment-title">Net Banking</span>
                      <span className="payment-subtitle">Supports all major Indian banks securely</span>
                    </div>
                    {paymentMethod === 'Netbanking' && <div className="payment-check-corner">✓</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 4: REVIEW CART ITEMS */}
            <div className="checkout-step-card glassmorphic-panel">
              <div className="step-card-header">
                <span className="step-card-number">4</span>
                <h3>Review Ordered Items</h3>
              </div>

              <div className="step-card-body">
                <div className="checkout-items-list">
                  {cartItems.map(item => {
                    const addonForState = getExpressAddonForCartItem(item, shippingState);
                    return (
                      <div key={item.id} className="checkout-item-row">
                        <div className="checkout-item-thumbnail">
                          <img src={item.image} alt={item.title} />
                        </div>
                        <div className="checkout-item-meta">
                          <h4 className="checkout-item-name">{item.title}</h4>
                          <div className="checkout-item-subtags">
                            <span className="checkout-qty-tag">Quantity: {item.qty}</span>
                            <span className="checkout-variant-tag">Variant Pricing</span>
                          </div>
                        </div>
                        <div className="checkout-item-price-block">
                          <span className="checkout-item-baseprice">
                            {convertPrice(item.price * item.qty, selectedCurr)}
                          </span>
                          {deliverySpeed === 'fast' && addonForState > 0 && (
                            <span className="checkout-item-addonprice">
                              + {convertPrice(addonForState * item.qty, selectedCurr)} Express Addon
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </section>

          {/* RIGHT: Floating Summary Sidebar */}
          <aside className="checkout-summary-section">
            <div className="sticky-summary-card glassmorphic-panel sticky-top-checkout">
              <h3 className="summary-title">Checkout Order Summary</h3>

              <div className="pricing-rows-group">
                <div className="pricing-summary-row">
                  <span className="row-label">Items Subtotal ({cartItems.reduce((acc, i) => acc + i.qty, 0)} items)</span>
                  <span className="row-value">{convertPrice(subtotal, selectedCurr)}</span>
                </div>

                {deliverySpeed === 'fast' && expressAddonTotal > 0 && (
                  <div className="pricing-summary-row highlight-row">
                    <span className="row-label">🚀 Express priority Addon</span>
                    <span className="row-value">+ {convertPrice(expressAddonTotal, selectedCurr)}</span>
                  </div>
                )}

                <div className="pricing-summary-row">
                  <span className="row-label">Standard Shipping Fee</span>
                  <span className="row-value">
                    {shipping === 0 ? (
                      <strong className="free-shipping-tag">FREE</strong>
                    ) : (
                      convertPrice(shipping, selectedCurr)
                    )}
                  </span>
                </div>

                <div className="pricing-summary-row">
                  <span className="row-label">GST / Applied Tax ({shippingSettings.tax_rate}%)</span>
                  <span className="row-value">{convertPrice(tax, selectedCurr)}</span>
                </div>

                <div className="summary-total-divider"></div>

                <div className="pricing-summary-row grand-total-row">
                  <span className="total-label">Grand Total Amount</span>
                  <span className="total-value">{convertPrice(grandTotal, selectedCurr)}</span>
                </div>
              </div>

              <form onSubmit={handleCheckoutSubmit} className="checkout-submit-form">
                <button
                  type="submit"
                  className="checkout-grand-cta-btn"
                  disabled={isPlacingOrder || checkoutAddresses.length === 0}
                >
                  <span className="cta-lock-icon"><FiLock /></span>
                  {isPlacingOrder ? (
                    'Processing Order...'
                  ) : paymentMethod === 'COD' ? (
                    `Place Order (COD) - ${convertPrice(grandTotal, selectedCurr)}`
                  ) : (
                    `Complete Payment - ${convertPrice(grandTotal, selectedCurr)}`
                  )}
                </button>
              </form>

              <div className="secure-checkout-certifications">
                <div className="cert-item">
                  <span className="cert-check">✓</span>
                  <span>Authentic Organic Products</span>
                </div>
                <div className="cert-item">
                  <span className="cert-check">✓</span>
                  <span>100% Secure SSL Gateway</span>
                </div>
                <div className="cert-item">
                  <span className="cert-check">✓</span>
                  <span>Hassle-Free Direct Refund</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
